import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import {
  Palette, BarChart3, Brush, Target,
  ArrowRight, Check, Sparkles
} from 'lucide-react';
import Footer from '../../components/home/Footer';

const SERVICES = [
  {
    icon: Palette,
    title: 'Identidade Visual',
    description: 'Logotipo profissional, paleta de cores, tipografia e manual de marca completo. Tudo que seu negócio precisa para se destacar visualmente.',
    features: ['Logotipo + variações', 'Paleta de cores', 'Tipografia', 'Manual de marca'],
    color: 'from-pink-500 to-rose-500',
    bgLight: 'bg-pink-50',
    iconColor: 'text-pink-500',
  },
  {
    icon: BarChart3,
    title: 'Gestão de Tráfego Pago',
    description: 'Campanhas estratégicas no Instagram, Facebook e Google Ads. Alcance o público certo e aumente suas vendas.',
    features: ['Estratégia de campanha', 'Criação de anúncios', 'Otimização contínua', 'Relatórios mensais'],
    color: 'from-blue-500 to-indigo-500',
    bgLight: 'bg-blue-50',
    iconColor: 'text-blue-500',
  },
  {
    icon: Brush,
    title: 'Branding',
    description: 'Posicionamento de marca, tom de voz, storytelling e comunicação visual que conecta com seu público.',
    features: ['Posicionamento de marca', 'Tom de voz', 'Storytelling', 'Guia de comunicação'],
    color: 'from-purple-500 to-violet-500',
    bgLight: 'bg-purple-50',
    iconColor: 'text-purple-500',
  },
  {
    icon: Target,
    title: 'Planejamento Estratégico',
    description: 'Análise de mercado, definição de metas, calendário editorial e estratégia de conteúdo personalizada.',
    features: ['Análise de mercado', 'Metas e KPIs', 'Calendário editorial', 'Estratégia de conteúdo'],
    color: 'from-amber-500 to-orange-500',
    bgLight: 'bg-amber-50',
    iconColor: 'text-amber-500',
  },
];

const ServicesLandingPage = () => {
  const { currentUser } = useAuth();

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-4 left-4 right-4 z-50">
        <div className="max-w-7xl mx-auto bg-white/50 backdrop-blur-md border border-white/30 rounded-[30px] px-4 sm:px-6 lg:px-8 shadow-lg">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img src="/logo.png" alt="hello, social" className="w-10 h-10 object-contain" />
            </Link>
            <nav className="hidden sm:flex items-center gap-8">
              <Link to="/" className="text-gray-800 hover:text-gray-900 font-medium transition-colors">Home</Link>
              <Link to="/services-landing" className="text-amber-600 font-semibold transition-colors">Serviços</Link>
              <Link to="/plans" className="text-gray-800 hover:text-gray-900 font-medium transition-colors">Planos</Link>
            </nav>
            <Link
              to={currentUser ? '/services' : '/register'}
              className="px-5 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-semibold rounded-full hover:from-amber-500 hover:to-orange-600 transition-all shadow-md hover:shadow-lg text-sm"
            >
              {currentUser ? 'Contratar' : 'Comece já'}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-2 mb-8">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-medium text-amber-700">Serviços Profissionais</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Leve seu negócio para o{' '}
            <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">próximo nível</span>
          </h1>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            A hello, social oferece serviços profissionais de marketing digital para impulsionar sua marca.
            Nossos especialistas cuidam de tudo para você.
          </p>
          <Link
            to={currentUser ? '/services' : '/register'}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold text-lg rounded-2xl hover:from-amber-500 hover:to-orange-600 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1"
          >
            {currentUser ? 'Ver Serviços' : 'Comece Agora'}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-4">
            Nossos Serviços
          </h2>
          <p className="text-gray-500 text-center mb-12 max-w-2xl mx-auto">
            Escolha o serviço ideal para o momento do seu negócio. Nosso time cuida de cada detalhe.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {SERVICES.map((service) => {
              const Icon = service.icon;
              return (
                <div key={service.title} className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-xl transition-all">
                  <div className={`w-14 h-14 ${service.bgLight} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className={`w-7 h-7 ${service.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-500 mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Sobre a hello, social</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Somos uma plataforma completa de marketing digital que une inteligência artificial com serviços profissionais.
            Além da criação automatizada de conteúdo, nosso time de especialistas está pronto para cuidar
            da identidade visual, estratégia e gestão de tráfego do seu negócio.
          </p>
          <Link
            to={currentUser ? '/services' : '/register'}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold rounded-2xl hover:from-amber-500 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl"
          >
            {currentUser ? 'Contratar Serviço' : 'Criar Conta Grátis'}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesLandingPage;
